import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReportModal from '../../components/molecules/ReportModal';

/**
 * Error type IDs that map to translation keys
 */
const ERROR_TYPES = [
  { id: 'typo', translationKey: 'error_option_typo' },
  { id: 'dont_know', translationKey: 'error_option_dont_know' },
  { id: 'blank', translationKey: 'error_option_blank' },
  { id: 'wrong_solution', translationKey: 'error_option_wrong_solution' },
  { id: 'images_not_loading', translationKey: 'error_option_images_not_loading' },
  { id: 'other', translationKey: 'error_option_other' },
];

/**
 * ErrorReportModal - Modal for reporting problems with practice exercises
 * Allows users to select a single error type and add an optional description
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onSubmit - Callback when report is submitted
 * @param {string} props.exerciseId - The exercise ID being reported
 * @param {string} props.activityInstanceId - The activity instance ID
 * @returns {JSX.Element} Rendered component
 */
function ErrorReportModal({ activityInstanceId = '', exerciseId = '', onClose, onSubmit, open }) {
  const { t } = useTranslation(['chatbot']);

  const options = useMemo(
    () =>
      ERROR_TYPES.map((errorType) => ({
        id: errorType.id,
        label: t(errorType.translationKey),
      })),
    [t]
  );

  const handleSubmit = useCallback(
    (data) => {
      const selectedOption = options.find((opt) => opt.id === data.selectedOptions[0]);

      onSubmit({
        exerciseId,
        activityInstanceId,
        errorType: selectedOption?.label || data.selectedOptions[0],
        errorTypeId: data.selectedOptions[0],
        description: data.comment || '',
      });
    },
    [exerciseId, activityInstanceId, onSubmit, options]
  );

  return (
    <ReportModal
      onClose={onClose}
      onSubmit={handleSubmit}
      open={open}
      options={options}
      selectionMode="single"
      showTextarea
      submitButtonText={t('Send')}
      textareaPlaceholder={t('error_description_placeholder')}
      title={t('error_report_title')}
    />
  );
}

ErrorReportModal.propTypes = {
  activityInstanceId: PropTypes.string,
  exerciseId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

export default ErrorReportModal;
